import type { ContractStatus } from "@/hooks/queries/org-admin-dashboard-types";
import { formatDate } from "@/lib/format-date";

// Story 14.12 (gh-680) — the ONE place the org-contract copy is written.
//
// There is deliberately no global FE error registry (the per-slice mapper
// convention: invitation-error.ts, seat-request-error.ts, …). The COPY, though,
// is a different thing from the mapping: an org_admin meets the same contract
// state on the dashboard banner, on the Add-person gate, and in the server's
// 403 ORG-010 answer, and three hand-written variants of it would drift into
// three different explanations of one situation. So the strings live here once
// and every surface imports them.
//
// Spec-driven — Screens 25/26 carry no contract banner, so the story's W3 field
// contract IS the source for this wording (13.6-AC3 / 14.7 precedent).

/**
 * Why the write path is closed, and what to do about it. Shared by BOTH
 * non-active states: the remedy is the same in each — the AMPS Director via
 * the (deliberately un-gated) Request-additional-seats CTA, and CITS for the
 * contract itself.
 */
export const CONTRACT_BLOCKED_BODY =
  "Inviting and member changes are disabled. Use Request additional seats to contact the AMPS Director — contract renewals are handled by CITS.";

/**
 * The not-yet-started half — nothing has lapsed, so the copy does not blame.
 *
 * `classify_contract_status` merges TWO causes into this one state: dates CITS
 * has not set yet, and a fully-dated window that has not opened. The merge is
 * correct server-side (same remedy, and the payload carries no start date to
 * tell them apart), so the copy has to hold for both — naming unset dates
 * would send the admin of a correctly-dated org to CITS about setup that is
 * already complete, and would never name the real remedy, which is the start
 * date arriving.
 */
export const CONTRACT_PENDING_TITLE = "Organization contract not active yet";
export const CONTRACT_PENDING_BODY =
  "Inviting and member changes unlock once your contract window is active.";

/**
 * Title for a refusal we learned about from the SERVER (403 ORG-010) rather
 * than from `contract_status`. ORG-010 fires on BOTH window edges and its
 * `details` carry no discriminator for them (`contract_end_date` plus a
 * server-side diagnostic line), so the wire cannot tell "lapsed" from "not
 * started yet" — this title is edge-neutral on purpose. Naming the
 * wrong edge would be worse than naming neither. The screens that HAVE the
 * status (dashboard, Add person) use the specific titles above/below instead;
 * this one is the backstop for a stale or missing dashboard read.
 */
export const CONTRACT_INACTIVE_TITLE = "Organization contract isn't active";

export interface ContractNotice {
  title: string;
  body: string;
}

// `contract_end_date` is a DATE, not a timestamp. `new Date("2025-12-31")`
// parses as UTC midnight and then renders in LOCAL time, so west of UTC the
// banner would name Dec 30 for a contract the backend still accepts writes on
// through Dec 31 — FR50 makes the end date inclusive, and two integration tests
// pin that exact edge. Append the time so the bare date parses as local
// midnight (same fix as ReviewSubmitStep's DOB formatter).
function formatContractDate(iso: string | null | undefined): string {
  if (!iso) return formatDate(iso);
  return formatDate(iso.includes("T") ? iso : `${iso}T00:00:00`);
}

/**
 * The banner/reason copy for a contract state, or null when the contract is
 * active (the ONLY state that renders nothing at all).
 */
export function contractNotice(
  status: ContractStatus | undefined,
  contractEndDate: string | null | undefined,
): ContractNotice | null {
  if (status === "expired") {
    return {
      title: `Organization contract ended ${formatContractDate(contractEndDate)}`,
      body: CONTRACT_BLOCKED_BODY,
    };
  }
  if (status === "not_started") {
    return { title: CONTRACT_PENDING_TITLE, body: CONTRACT_PENDING_BODY };
  }
  // `active` — and also `undefined`, i.e. the dashboard has not loaded yet. A
  // screen must not gate on a state it has not read: the server-side guard is
  // the enforcement, and pre-emptively blocking on a pending query would show
  // every org_admin a contract warning for the length of one fetch.
  return null;
}
